import { GrPlan } from 'react-icons/gr';
import { MdLanguage } from 'react-icons/md';
import { RiAwardLine, RiContactsLine, RiStore2Line } from 'react-icons/ri';
import {
  OracleOnboardingMultiDocumentSection,
  OracleOnboardingSection
} from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { MultiDocumentLayout } from './multi-document-constants';

// Common sentence structure for every section's empty state. `sourceDescriptor` is authored
// per section (article included) -- it doesn't always match `label` (e.g. About the Business's
// descriptor is "a business overview").
const emptyStateCopyFor = (sourceDescriptor: string): string =>
  `Connect ${sourceDescriptor} source to have the AI read your stuff and make cool things.`;

export const STAKEHOLDERS_INTERNAL_KEY = 'stakeholders-internal';
export const STAKEHOLDERS_EXTERNAL_KEY = 'stakeholders-external';

// Entries with a multi-document section key hold many documents, each with a note. Other entries
// carrying `addButtonLabel` are document-backed: a source file is uploaded and the AI summarises it.
// The stakeholder entries render their own card.
export const ONBOARDING_SECTIONS = [
  {
    key: OracleOnboardingSection.ABOUT_THE_BUSINESS,
    label: 'About the Business',
    icon: <RiStore2Line />,
    emptyStateCopy: emptyStateCopyFor('a business overview'),
    addButtonLabel: 'Add Business Overview'
  },
  {
    key: OracleOnboardingSection.ACCOUNT_PLAN,
    label: 'Account Plan',
    icon: <GrPlan />,
    emptyStateCopy: emptyStateCopyFor('an account plan'),
    addButtonLabel: 'Add Account Plan'
  },
  {
    key: STAKEHOLDERS_INTERNAL_KEY,
    label: 'Stakeholders — Internal',
    icon: <RiContactsLine />
  },
  {
    key: STAKEHOLDERS_EXTERNAL_KEY,
    label: 'Stakeholders — External',
    icon: <RiContactsLine />
  },
  {
    key: OracleOnboardingSection.RELATIONSHIP_PREFERENCES,
    label: 'Relationship & Preferences',
    icon: <MdLanguage />,
    emptyStateCopy: emptyStateCopyFor('a relationship & preferences'),
    addButtonLabel: 'Add Relationship & Preferences'
  },
  {
    key: OracleOnboardingMultiDocumentSection.STANDARDS_AND_PRACTICES,
    label: 'Standards & Practices (S&P)',
    icon: <RiStore2Line />,
    addButtonLabel: 'Add Resource',
    footerCopy: 'Reference only — nothing else in the app reads this.',
    itemNoun: 'resource',
    removeBody: 'It will no longer be listed in Standards & Practices.',
    modal: {
      itemTitle: 'Resource',
      addLabel: 'Add resource',
      noteLabel: 'Note',
      noteHelp: 'Required — what this resource covers, so the team knows when to use it.'
    },
    body: {
      layout: MultiDocumentLayout.TABLE,
      intro:
        'The client’s standards & practices — claim-substantiation and compliance documentation. For regulated categories like health and financial services, keep the substantiation library, legal-approved claims, and regulatory guidelines linked here so the team and the AI can reference what a claim needs before it goes out.',
      table: { heading: 'Resources', itemColumnLabel: 'Resource', noteColumnLabel: 'Note' }
    }
  },
  {
    key: OracleOnboardingMultiDocumentSection.EXAMPLES_OF_EXCELLENCE,
    label: 'Examples of Excellence',
    icon: <RiAwardLine />,
    addButtonLabel: 'Add Example of Excellence',
    footerCopy: 'This content informs Recaps and Agendas & Call Prep',
    itemNoun: 'example',
    removeBody: 'It will no longer inform Recaps or Agendas.',
    modal: {
      itemTitle: 'Example of Excellence',
      addLabel: 'Add example',
      noteLabel: 'Why is this an example of excellence?',
      noteHelp: 'Required — what made it land, so someone reusing it knows what to copy.'
    },
    body: {
      layout: MultiDocumentLayout.LIST,
      emptyStateCopy: 'Add examples of great work so the AI knows what good looks like.'
    }
  }
] as const;

export const ONBOARDING_MENU_ITEMS = ONBOARDING_SECTIONS.map(({ key, label, icon }) => ({
  key,
  label,
  icon
}));
